/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Button, Tooltip } from '@components/atoms'
import {
	AdminEditMenuModal,
	AdminEditMenuIcon,
	AdminEditMenuId,
	AdminEditMenuPath,
	AdminEditMenuActivateToggle,
} from '@components/systems'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import { useNavigate } from 'react-router-dom'
import {
	useAppDispatch,
	useAppSelector,
	EditMenuType,
	adminActions,
	AdminStateType,
	CrudType,
} from '@redux/index'
import { GlobalComponentsContext } from '@contexts'

/** 메뉴 편집 폼 — 아이콘 → 이름 → 경로 → 활성. 바닥에 삭제(고스트) · 저장(블랙 하나) */
export const AdminEditMenu = () => {
	// hook
	const navigate = useNavigate()
	const { showToast } = React.useContext(GlobalComponentsContext)

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const editMenuMode: AdminStateType['editMenuMode'] = useAppSelector(
		state => state.admin.editMenuMode,
	)
	const triggerCreateMenuSuccessResult: boolean = useAppSelector(
		state => state.admin.triggerCreateMenuSuccessResult,
	)
	const triggerModifyMenuSuccessResult: boolean = useAppSelector(
		state => state.admin.triggerModifyMenuSuccessResult,
	)
	const triggerDeleteMenuSuccessResult: boolean = useAppSelector(
		state => state.admin.triggerDeleteMenuSuccessResult,
	)

	// variables
	const isDir: boolean = editTempMenu.id.startsWith('MD-')
	const selectedDirName: string = editTempMenu.name ?? ''
	const isAllowDeleteDir: boolean =
		crud.includes('D') !== null && editMenuMode === 'update' && isDir
	const isAllowDeleteObject: boolean = crud.includes('D') !== null && !isDir
	const isAllowUpdateDir: boolean = isDir && crud.includes('U') !== null
	const isAllowUpdateObject: boolean = !isDir && crud.includes('U') !== null
	const isReadOnly = isDir ? !isAllowUpdateDir : !isAllowUpdateObject

	React.useEffect(() => {
		if (
			['create-dir', 'create-object'].includes(editMenuMode) &&
			triggerCreateMenuSuccessResult
		) {
			navigate(-1)
			showToast(`'${selectedDirName}' 폴더가 생성되었습니다.`)
			reduxDispatch(adminActions.TRIGGER_CREATE_MENU_SUCCESS_RESULT(false))
			reduxDispatch(adminActions.EDIT_MENU_MODE('update'))
		}
	}, [triggerCreateMenuSuccessResult, editMenuMode])

	React.useEffect(() => {
		if (editMenuMode === 'update' && triggerModifyMenuSuccessResult) {
			showToast(`'${selectedDirName}' 폴더가 업데이트 되었습니다.`)
			reduxDispatch(adminActions.TRIGGER_MODIFY_MENU_SUCCESS_RESULT(false))
		}
	}, [triggerModifyMenuSuccessResult, editMenuMode])

	React.useEffect(() => {
		if (editMenuMode === 'update' && triggerDeleteMenuSuccessResult) {
			showToast('폴더가 삭제 되었습니다.')
			reduxDispatch(adminActions.TRIGGER_DELETE_MENU_SUCCESS_RESULT(false))
		}
	}, [triggerDeleteMenuSuccessResult, editMenuMode])

	const onSubmitClick = () => {
		if (['create-dir', 'create-object'].includes(editMenuMode)) {
			reduxDispatch(adminActions.TRIGGER_CREATE_MENU(true))
		} else {
			reduxDispatch(adminActions.MENU_OPEN_MODAL('updatingSubmit'))
		}
	}

	const onDeleteDirClick = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL('deletingDirSubmit'))
	}

	const onDeleteObjectClick = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL('deletingObjectSubmit'))
	}

	return (
		<View css={style}>
			<View className="menuDetailView">
				<View className="menuDetailInnerView">
					<AdminEditMenuIcon readOnly={isReadOnly} />
					<AdminEditMenuId readOnly={isReadOnly} />
					<AdminEditMenuPath readOnly={isReadOnly} />
					<AdminEditMenuActivateToggle readOnly={isReadOnly} />
				</View>

				<View className="bottomView">
					{isAllowDeleteDir ? (
						<Tooltip title="폴더 삭제">
							<Button
								variant="outlined"
								className="deleteMenuButton"
								onClick={onDeleteDirClick}
								disabled={editTempMenu.name === ''}
							>
								삭제
							</Button>
						</Tooltip>
					) : null}
					{isAllowDeleteObject ? (
						<Tooltip title="오브젝트 삭제">
							<Button
								variant="outlined"
								className="deleteMenuButton"
								onClick={onDeleteObjectClick}
								disabled={editTempMenu.name === ''}
							>
								삭제
							</Button>
						</Tooltip>
					) : null}

					{!isReadOnly ? (
						<Tooltip title="저장">
							<Button
								title="저장"
								variant="contained"
								onClick={onSubmitClick}
								disabled={editTempMenu.name === ''}
							>
								저장
							</Button>
						</Tooltip>
					) : null}
				</View>
			</View>

			<AdminEditMenuModal />
		</View>
	)
}

const style = css({
	width: '100%',
	fontFamily: boFont.ui,

	'.menuDetailView': {
		width: '100%',

		'.menuDetailInnerView': {
			width: '100%',
		},

		'.bottomView': {
			display: 'flex',
			justifyContent: 'flex-end',
			alignItems: 'center',
			gap: 8,
			marginTop: 32,
			paddingTop: 20,
			borderTop: `1px solid ${mono.line}`,
		},
	},
})
